import {useCallback,useEffect,useRef,useState} from 'react';
import {commerce} from '../api/commerce';
import {openRazorpayCheckout,validGatewayOrder} from './razorpayCheckout';
import {readOperation,beginOperation,saveOperation,clearOperation,operationLock} from './commerceRecovery';
import {commerceError,commerceErrorCode,knownUncharged} from './commerce';
import {validateCheckout,validateTopUpResponse} from './commerceValidation';
import useCommerceScope from './useCommerceScope';
import useStatusPolling from './useStatusPolling';
export default function useCommerceOperation(user,type,onConfirmed,{knownId}={}){
 const scope=useCommerceScope(user),identity=scope.account;
 const [record,setRecord]=useState(null),[packet,setPacket]=useState(null),[busy,setBusy]=useState(false),[error,setError]=useState('');
 const guard=useRef(false);
 const sync=useCallback(()=>{
  try{if(!identity)throw new Error('Sign in again to confirm your account.');const value=readOperation(identity,type);setRecord(!knownId||value?.id===knownId?value:null);}
  catch(failure){setError(failure.message);}
 },[identity,type,knownId]);
 useEffect(()=>{setRecord(null);setPacket(null);setError('');sync();},[sync]);
 const run=async(payload,resume=false,statusOnly=false,context)=>{
  if(guard.current)return;guard.current=true;setBusy(true);setError('');let current,outcome;
  const active=()=>scope.current();
  try{
   if(!identity)throw new Error('Sign in again to confirm your account.');
   if(user?.phoneVerified!==true)throw new Error('Verify your phone before continuing with this payment.');
   await operationLock(identity,type,async()=>{
    if(!active())return;
    current=readOperation(identity,type);
    if(knownId&&current?.id!==knownId)current=null;
    const readOnlyKnown=Boolean(knownId&&!current);
    if(!current&&!readOnlyKnown){if(!payload)throw new Error('No unfinished payment found.');current=beginOperation(identity,type,payload,context);setPacket(null);}
    if(current)setRecord(current);
    if(statusOnly&&!current?.id&&!readOnlyKnown)return;
    const top=type==='topUp',id=current?.id||knownId;
    const status=top?commerce.topUpStatus:commerce.checkoutStatus;
    let value=id?await status(id):await(top?commerce.topUp:commerce.checkout)(current.payload,current.key);
    if(!active())return;
    const receivedId=top?value?.topUpId:value?.checkoutId;
    if(typeof receivedId!=='string'||!receivedId.trim()||(id&&receivedId!==id))throw new Error('Unable to identify this payment. Check status before retrying.');
    if(current){current=saveOperation({...current,id:receivedId});setRecord(current);}
    const accept=async next=>{
     (top?validateTopUpResponse:validateCheckout)(next);
     if(!active())return true;
     outcome=next;
     setPacket({account:identity,value:next});
     if(next.status===(top?'SUCCESS':'CONFIRMED')){
      if(current){clearOperation(current);setRecord(null);}
      if(active())await onConfirmed?.(next);
      return true;
     }
     if(['FAILED','CANCELLED'].includes(next.status)){if(current){clearOperation(current);setRecord(null);}return true;}
     return false;
    };
    if(await accept(value))return;
    if(resume&&value.paymentStatus!=='REFUND_PENDING'&&(top?value.status==='PENDING':value.status==='PAYMENT_PENDING')&&validGatewayOrder(value.gatewayOrder)){
     if(readOnlyKnown){
      if(readOperation(identity,type))throw new Error('Another payment is saved in this browser. Resolve it before resuming this payment.');
      current=saveOperation({...beginOperation(identity,type,{recoveryId:id}),id,recoveryOnly:true});setRecord(current);
     }
     const proof=await openRazorpayCheckout({order:value.gatewayOrder,description:top?'Wallet recharge':'Delivery booking'});
     if(!active())return;
     value=await(top?commerce.verifyTopUp:commerce.verifyCheckout)(current.id,{paymentId:proof.razorpay_payment_id,signature:proof.razorpay_signature},current.key+'-verify');
     if(!active())return;
     if((top?value?.topUpId:value?.checkoutId)!==current.id)throw new Error('Unable to confirm payment identity.');
     await accept(value);
    }
   });
  }catch(failure){if(active()){
   const pending=failure?.response?.data?.pendingOperation;
   const redirect=commerceErrorCode(failure)==='PAYMENT_RECONCILIATION_REQUIRED'&&knownUncharged(failure)&&!current?.id&&
    typeof pending?.id==='string'&&pending.id.trim()&&['CHECKOUT','TOPUP'].includes(pending.kind);
   if(current&&knownUncharged(failure)){clearOperation(current);setRecord(null);}
   if(redirect){
    const targetType=pending.kind==='CHECKOUT'?'checkout':'topUp';
    try{
     const existing=readOperation(identity,targetType);
     if(existing?.id&&existing.id!==pending.id)throw new Error('Another payment is saved. Review it before continuing.');
     const pointer=saveOperation({...beginOperation(identity,targetType,{recoveryId:pending.id}),id:pending.id,recoveryOnly:true,context:undefined});
     if(targetType===type){setRecord(pointer);setPacket(null);setError('');}
     else setError('A wallet recharge or booking is already pending. Open the existing payment using the recovery link.');
    }catch(recoveryFailure){setError(recoveryFailure.message);}
   }else setError(commerceError(failure));
  }}
  finally{guard.current=false;if(active())setBusy(false);}
  return outcome;
 };
 const visibleRecord=record?.account===identity?record:null,result=packet?.account===identity?packet.value:null;
 const recoveryId=visibleRecord?.id||knownId;
 useStatusPolling(async()=>{if(!guard.current)await run(undefined,false,true);},Boolean(recoveryId)&&(!result||['PENDING','PAYMENT_PENDING','BOOKING_PENDING'].includes(result.status)),identity+':'+type+':'+(recoveryId||''));
 const runRef=useRef(run);runRef.current=run;
 useEffect(()=>{if(knownId)runRef.current(undefined,false,true);},[identity,type,knownId]);
 return {record:visibleRecord,result,busy,error,sync,canRecoverKnown:Boolean(knownId),start:(payload,context)=>run(payload,true,false,context),check:()=>run(),resume:()=>run(undefined,true)};
}
